import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Package, Search, ArrowUpDown, ArrowUp, ArrowDown, Filter, ChevronDown, ChevronUp, X, Tag, FileText, Users, Leaf } from 'lucide-react'
import { useArticlesWithDefaultPrices, useArticleCategories } from '../../hooks/useSupabase'

const ARTICLES_FILTER_STORAGE_KEY = 'articles-advanced-filters'

interface ArticleFilters {
  showAdvancedFilters: boolean
  selectedCategories: string[]
  selectedTypes: string[]
  selectedVatRates: number[]
  selectedProvisionArticle: string | null  // 'yes', 'no', or null
  itemsPerPage: number
}

const getSavedFilters = (): ArticleFilters | null => {
  try {
    const saved = sessionStorage.getItem(ARTICLES_FILTER_STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.error('Error loading saved filters:', e)
  }
  return null
}

const articleTypes = [
  { key: 'service', name: 'Tjänst' },
  { key: 'product', name: 'Produkt' },
  { key: 'hourly', name: 'Timdebitering' }
]

const vatRates = [
  { value: 25, label: '25%' },
  { value: 12, label: '12%' },
  { value: 6, label: '6%' },
  { value: 0, label: '0%' }
]

export function AdminArticles() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<string>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Advanced filter state with sessionStorage restoration
  const savedFilters = getSavedFilters()
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(savedFilters?.showAdvancedFilters ?? false)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(savedFilters?.selectedCategories ?? [])
  const [selectedTypes, setSelectedTypes] = useState<string[]>(savedFilters?.selectedTypes ?? [])
  const [selectedVatRates, setSelectedVatRates] = useState<number[]>(savedFilters?.selectedVatRates ?? [])
  const [selectedProvisionArticle, setSelectedProvisionArticle] = useState<string | null>(savedFilters?.selectedProvisionArticle ?? null)
  const [itemsPerPage, setItemsPerPage] = useState<number>(savedFilters?.itemsPerPage ?? 20)

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Persist filters to sessionStorage
  useEffect(() => {
    const filters: ArticleFilters = {
      showAdvancedFilters,
      selectedCategories,
      selectedTypes,
      selectedVatRates,
      selectedProvisionArticle,
      itemsPerPage
    }
    sessionStorage.setItem(ARTICLES_FILTER_STORAGE_KEY, JSON.stringify(filters))
  }, [showAdvancedFilters, selectedCategories, selectedTypes, selectedVatRates, selectedProvisionArticle, itemsPerPage])

  // Använd Supabase hooks istället för lokal state
  const { data: articles = [], isLoading, error } = useArticlesWithDefaultPrices()
  const { data: categories = [] } = useArticleCategories()

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Ingen kategori'
    const category = categories.find(c => c.id === categoryId)
    return category?.name || 'Okänd kategori'
  }

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#6B7280'
    const category = categories.find(c => c.id === categoryId)
    return category?.color || '#6B7280'
  }

  const getTypeName = (type: string | null) => {
    if (!type) return 'Tjänst'
    const typeObj = articleTypes.find(t => t.key === type)
    return typeObj?.name || type
  }

  // Count active filters
  const activeFilterCount = [
    selectedCategories.length > 0,
    selectedTypes.length > 0,
    selectedVatRates.length > 0,
    selectedProvisionArticle !== null
  ].filter(Boolean).length

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCategories([])
    setSelectedTypes([])
    setSelectedVatRates([])
    setSelectedProvisionArticle(null)
  }

  // Filter articles based on search term and advanced filters
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.description && article.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        getCategoryName(article.category).toLowerCase().includes(searchTerm.toLowerCase())

      // Category filter
      const matchesCategory = selectedCategories.length === 0 ||
        selectedCategories.includes(article.category || '')

      // Type filter
      const matchesType = selectedTypes.length === 0 ||
        selectedTypes.includes(article.type || 'service')

      // VAT rate filter
      const matchesVat = selectedVatRates.length === 0 ||
        selectedVatRates.includes(article.vat_rate ?? 25)

      // Provision article filter
      const matchesProvision = selectedProvisionArticle === null ||
        (selectedProvisionArticle === 'yes' && article.is_provision_article) ||
        (selectedProvisionArticle === 'no' && !article.is_provision_article)

      return matchesSearch && matchesCategory && matchesType && matchesVat && matchesProvision
    })
  }, [articles, searchTerm, selectedCategories, selectedTypes, selectedVatRates, selectedProvisionArticle])

  // Sort articles
  const sortedArticles = useMemo(() => {
    return [...filteredArticles].sort((a, b) => {
      let aValue: any = sortField === 'category' ? getCategoryName(a.category) : a[sortField as keyof typeof a]
      let bValue: any = sortField === 'category' ? getCategoryName(b.category) : b[sortField as keyof typeof b]

      if (typeof aValue === 'string') aValue = aValue.toLowerCase()
      if (typeof bValue === 'string') bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredArticles, sortField, sortDirection])

  // Apply items per page limit
  const displayedArticles = useMemo(() => {
    if (itemsPerPage === 0) return sortedArticles // 0 = Show all
    return sortedArticles.slice(0, itemsPerPage)
  }, [sortedArticles, itemsPerPage])

  // Navigate to article and store filtered IDs for prev/next navigation
  const handleArticleClick = (articleId: string) => {
    const articleIds = sortedArticles.map(a => a.id)
    sessionStorage.setItem('filtered-article-ids', JSON.stringify(articleIds))
    navigate(`/admin/articles/${articleId}`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar artiklar...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Fel vid laddning av artiklar</div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100">Artiklar</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/articles/categories')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-lg font-medium transition-colors"
          >
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Kategorier</span>
          </button>
          <button
            onClick={() => navigate('/admin/pricelists')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg font-medium transition-colors"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Prislistor</span>
          </button>
          <button
            onClick={() => navigate('/admin/articles/new')}
            className="btn-primary flex items-center justify-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Lägg till</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Sök artiklar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
        {/* Filters row */}
        <div className="flex items-center gap-2">
          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
              activeFilterCount > 0
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'border-nature-300 dark:border-nature-600 bg-[#faf9f7] dark:bg-nature-700 text-nature-700 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-600'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Avancerade filter</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
            {showAdvancedFilters ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-md bg-[#faf9f7] dark:bg-nature-700 text-nature-900 dark:text-nature-100"
          >
            <option value={10}>10 st</option>
            <option value={20}>20 st</option>
            <option value={50}>50 st</option>
            <option value={100}>100 st</option>
            <option value={0}>Alla</option>
          </select>
          <span className="text-xs text-nature-500 dark:text-nature-400 whitespace-nowrap">
            ({filteredArticles.length})
          </span>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Category Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <Tag className="inline h-4 w-4 mr-1" />
                  Kategori
                  {selectedCategories.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCategories.length})</span>
                  )}
                </label>
                {selectedCategories.length > 0 && (
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {categories.map(category => (
                  <label key={category.id} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCategories([...selectedCategories, category.id])
                        } else {
                          setSelectedCategories(selectedCategories.filter(c => c !== category.id))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span
                      className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                      style={{ backgroundColor: category.color }}
                    >
                      {category.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Type Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Typ
                  {selectedTypes.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedTypes.length})</span>
                  )}
                </label>
                {selectedTypes.length > 0 && (
                  <button
                    onClick={() => setSelectedTypes([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {articleTypes.map(type => (
                  <label key={type.key} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTypes([...selectedTypes, type.key])
                        } else {
                          setSelectedTypes(selectedTypes.filter(t => t !== type.key))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{type.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* VAT Rate Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Momssats
                  {selectedVatRates.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedVatRates.length})</span>
                  )}
                </label>
                {selectedVatRates.length > 0 && (
                  <button
                    onClick={() => setSelectedVatRates([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {vatRates.map(vat => (
                  <label key={vat.value} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedVatRates.includes(vat.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedVatRates([...selectedVatRates, vat.value])
                        } else {
                          setSelectedVatRates(selectedVatRates.filter(v => v !== vat.value))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{vat.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Provision Article Filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-nature-700 dark:text-nature-300">
                  Provisionsartikel
                </label>
                {selectedProvisionArticle !== null && (
                  <button
                    onClick={() => setSelectedProvisionArticle(null)}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                  <input
                    type="radio"
                    name="provisionArticle"
                    checked={selectedProvisionArticle === 'yes'}
                    onChange={() => setSelectedProvisionArticle('yes')}
                    className="h-4 w-4 text-primary-600 border-nature-300"
                  />
                  <span className="text-sm text-nature-700 dark:text-nature-200">Ja</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                  <input
                    type="radio"
                    name="provisionArticle"
                    checked={selectedProvisionArticle === 'no'}
                    onChange={() => setSelectedProvisionArticle('no')}
                    className="h-4 w-4 text-primary-600 border-nature-300"
                  />
                  <span className="text-sm text-nature-700 dark:text-nature-200">Nej</span>
                </label>
              </div>
            </div>
          </div>

          {/* Clear filters button */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-600 flex justify-end">
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              >
                <X className="h-4 w-4" />
                Rensa alla filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Articles List */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
          {displayedArticles.map((article) => (
            <div
              key={article.id}
              onClick={() => handleArticleClick(article.id)}
              className="p-4 cursor-pointer active:bg-nature-100 dark:active:bg-nature-700"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-mono text-nature-500 dark:text-nature-400 mr-2">{article.article_number}</span>
                  <span className="font-medium text-nature-900 dark:text-nature-100">{article.name}</span>
                </div>
              </div>
              {article.description && (
                <div className="text-sm text-nature-600 dark:text-nature-400 mb-2 line-clamp-2">
                  {article.description}
                </div>
              )}
              {/* Prices */}
              <div className="flex items-center gap-4 mb-2 text-sm">
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Users className="h-3 w-3" />
                  {article.customerPrice ? `${article.customerPrice.toLocaleString('sv-SE')} kr` : '-'}
                </span>
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Leaf className="h-3 w-3" />
                  {article.subcontractorPrice ? `${article.subcontractorPrice.toLocaleString('sv-SE')} kr` : '-'}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                  style={{ backgroundColor: getCategoryColor(article.category) }}
                >
                  {getCategoryName(article.category)}
                </span>
                <span className="px-2 py-0.5 text-xs rounded-full bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 font-medium">
                  {getTypeName(article.type)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-nature-50 dark:bg-nature-700">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('article_number')}
                >
                  <div className="flex items-center gap-1">
                    Nr
                    {sortField === 'article_number' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Artikel
                    {sortField === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">Beskrivning</th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('customerPrice')}
                >
                  <div className="flex items-center justify-end gap-1">
                    <Users className="h-3 w-3 text-blue-500" />
                    Kundpris
                    {sortField === 'customerPrice' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('subcontractorPrice')}
                >
                  <div className="flex items-center justify-end gap-1">
                    <Leaf className="h-3 w-3 text-green-500" />
                    TG-pris
                    {sortField === 'subcontractorPrice' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#faf9f7] dark:bg-nature-800 divide-y divide-nature-200 dark:divide-nature-700">
              {displayedArticles.map((article) => (
                <tr
                  key={article.id}
                  onClick={() => handleArticleClick(article.id)}
                  className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors duration-200 cursor-pointer"
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-xs font-mono text-nature-600 dark:text-nature-400">{article.article_number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                      {article.name}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                        style={{ backgroundColor: getCategoryColor(article.category) }}
                      >
                        {getCategoryName(article.category)}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 font-medium">
                        {getTypeName(article.type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-nature-600 dark:text-nature-400 max-w-xs truncate">
                    {article.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {article.customerPrice ? `${article.customerPrice.toLocaleString('sv-SE')} kr` : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {article.subcontractorPrice ? `${article.subcontractorPrice.toLocaleString('sv-SE')} kr` : '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {displayedArticles.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-nature-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
            {searchTerm || activeFilterCount > 0 ? 'Inga artiklar hittades' : 'Inga artiklar än'}
          </h3>
          <p className="text-nature-600 dark:text-nature-400">
            {searchTerm || activeFilterCount > 0 ? 'Prova att ändra söktermen eller filtren' : 'Lägg till din första artikel för att komma igång'}
          </p>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Rensa alla filter
            </button>
          )}
        </div>
      )}
    </div>
  )
}
